import { format, parseValue } from "numfmt";

const VALID_DATE_FORMATS = [
  "mm/dd/yyyy",
  "m/d/yyyy",
  "m/dd/yyyy",
  "mm/d/yyyy",
  "mm/dd/yy",
  "m/d/yy",
  "m/dd/yy",
  "mm/d/yy",
  "yyyy/mm/dd",
  "yyyy/m/d",
  "yyyy/m/dd",
  "yyyy/mm/d",
  "yyyy-mm-dd",
  "yyyy-m-d",
  "yyyy-mm-d",
  "yyyy-m-dd",
  "mm-dd-yyyy",
  "m-d-yyyy",
  "mm-d-yyyy",
  "m-dd-yyyy",
  "mm-dd-yy",
  "m-d-yy",
  "m-dd-yy",
  "mm-d-yy",
];
const VALID_TIME_FORMATS = ["hh:mm:ss", "hh:mm:ss AM/PM", "h:mm:ss", "h:mm:ss AM/PM", "yyyy-mm-dd hh:mm:ss AM/PM"];

const NumberFormats = {
  Number: "number",
  Currency: "currency",
  Date: "date",
  ShortDate: "shortdate",
  LongDate: "longdate",
  Time: "time",
  Percentage: "percentage",
  Fraction: "fraction",
  Text: "text",
  Custom: "custom",
  General: "general",
};
const LONG_TIME_FORMAT = "yyyy-mm-dd hh:mm:ss AM/PM";
const SHORT_TIME_FORMAT = "HH:mm:ss AM/PM";

const SHORT_DATE_FORMAT = "mm/dd/yyyy";
const LONG_DATE_FORMAT = "dddd, mmmm dd, yyyy";

const EXCEL_JS_DATE_DIFF = 25568;
const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
Object.freeze(NumberFormats);

const isValidDate = (text) => {
  const parsed = parseValue(text);
  return parsed && VALID_DATE_FORMATS.includes(parsed.z);
};
const isValidCurrency = (text) => {
  return /^(\$\s*\d+(\.\d{2})?|\d+(\.\d{2})?\s*\$)$/.test(text.trim());
};
const isValidPercentage = (text) => {
  return /^(\d+(\.\d+)?\s*%|%\s*\d+(\.\d+)?)$/.test(text.trim());
};
const isValidTime = (text) => {
  const parsed = parseValue(text);
  return parsed && VALID_TIME_FORMATS.includes(parsed.z);
};

const mixedFractionRegex = /^-?\d+\s+\d+\/\d+$/;
// Test function
function isValidFraction(value) {
  if (!mixedFractionRegex.test(value)) return false;

  // Extract parts
  const parts = value.trim().replace(/^-/, "").split(" ");

  const [_, numerator, denominator] = [parts[0], ...parts[1].split("/")].map(Number);
  // Check for division by zero and numerator < denominator
  return denominator !== 0;
}

// This function is not used in the code
const getDateNumber = (text) => {
  return Math.ceil(new Date(text).getTime() / MILLISECONDS_IN_A_DAY + EXCEL_JS_DATE_DIFF + 1);
};
const getCurrencyNumber = (text) => {
  return text.match(/-?(?:\d+|\d*\.\d+)/)[0];
};

// This function is not used in the code
const getPercentageNumber = (text) => {
  return text.match(/-?(?:\d+|\d*\.\d+)/)[0];
};
const getTimeNumber = (text) => {
  const parsed = parseValue(text);
  if (parsed && VALID_TIME_FORMATS.includes(parsed.z)) {
    return parsed.v;
  }
  return text;
};
function getFractionNumber(fraction) {
  // Check if it's a valid mixed fraction
  const mixedFractionRegex = /^-?\d+\s+\d+\/\d+$/;
  if (!mixedFractionRegex.test(fraction)) {
    throw new Error("Invalid mixed fraction format");
  }

  // Extract parts
  const isNegative = fraction.startsWith("-");
  const parts = fraction.trim().replace(/^-/, "").split(" ");
  const wholeNumber = parseInt(parts[0]);
  const [numerator, denominator] = parts[1].split("/").map(Number);

  // Check for division by zero
  if (denominator === 0) {
    throw new Error("Division by zero");
  }

  // Calculate decimal
  const decimal = wholeNumber + numerator / denominator;
  return isNegative ? -decimal : decimal;
}
function getEditModeValue(dataType, text) {
  if (String(text).trim().startsWith("=") || !text) return text;
  switch (dataType) {
    case NumberFormats.ShortDate:
    case NumberFormats.Date:
    case NumberFormats.LongDate:
      if (!isNaN(text)) return formatDate(text, NumberFormats.ShortDate);
      break;
    case NumberFormats.Currency:
      return getCurrencyNumber(text + "");
    case NumberFormats.Percentage:
      return text * 100 + "%";
    case NumberFormats.Time:
      return +text >= 1 ? format(LONG_TIME_FORMAT, +text) : format(SHORT_TIME_FORMAT, +text);
  }
  return text;
}

const formatFraction = (number) => {
  if (+number === 0) return number;

  const integerPart = Math.trunc(number);
  const fractionalPart = Math.abs(number - integerPart);
  if (fractionalPart === 0) return integerPart;

  const denominator = 100;
  const numerator = Math.round(fractionalPart * denominator);

  const gcd = findGCD(numerator, denominator);
  const simplifiedNumerator = Math.ceil(numerator / gcd);
  const simplifiedDenominator = Math.ceil(denominator / gcd);
  return integerPart === 0
    ? (Object.is(integerPart, -0) ? `- ` : "") + `${simplifiedNumerator}/${simplifiedDenominator}`
    : `${integerPart} ${simplifiedNumerator}/${simplifiedDenominator}`;
};
function findGCD(a, b) {
  if (b === 0) {
    return a;
  }
  return findGCD(b, a % b);
}
const formatTime = (decimal) => {
  if (isNaN(decimal)) return decimal;
  return format(SHORT_TIME_FORMAT, +decimal);
};
const formatCurrency = (number) => {
  const roundedNumber = Math.round(+number * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD", // Change the currency code as needed (e.g., 'EUR', 'GBP', etc.)
  }).format(roundedNumber);
};

export const formatDate = (numDate, type) => {
  if (isNaN(numDate)) return numDate;
  switch (type) {
    case NumberFormats.Date:
    case NumberFormats.ShortDate:
      return format(SHORT_DATE_FORMAT, +numDate);
    case NumberFormats.LongDate:
      return format(LONG_DATE_FORMAT, +numDate);
    default:
      return numDate;
  }
};

export {
  NumberFormats,
  formatFraction,
  formatTime,
  formatCurrency,
  isValidDate,
  isValidTime,
  getDateNumber,
  isValidCurrency,
  getCurrencyNumber,
  isValidPercentage,
  getPercentageNumber,
  isValidFraction,
  getFractionNumber,
  getEditModeValue,
  getTimeNumber,
  VALID_DATE_FORMATS,
  VALID_TIME_FORMATS,
};
